import { useState, useCallback, useRef, useEffect } from 'react';
import useEmployeeProducts from '../../hooks/useEmployeeProducts';
import { PRODUCT_CATEGORIES, EMPLOYEE_PRODUCTS_PAGE_SIZE } from '../../utils/constants';
import ProductForm from '../../components/employee/ProductForm';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import MultiSelect from '../../components/common/MultiSelect';
import { formatDate } from '../../utils/formatters';
import './EmployeeListPage.css';
import './Products.css';

const PAGE_SIZE = EMPLOYEE_PRODUCTS_PAGE_SIZE;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const INVENTORY_STATUS_OPTIONS = [
  { value: 'in_stock', label: 'In Stock' },
  { value: 'low_inventory', label: 'Low Inventory' },
  { value: 'out_of_stock', label: 'Out of Stock' },
];


const EmployeeProducts = () => {
  // ── View state ───────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingProduct, setEditingProduct] = useState(null);

  // ── Bulk selection ───────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategoryIds, setBulkCategoryIds] = useState([]);

  // ── List filters ─────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const debounceRef = useRef(null);

  // Refs to avoid stale closures in debounced callbacks
  const activeFilterRef = useRef(activeFilter);
  // eslint-disable-next-line react-hooks/refs
  activeFilterRef.current = activeFilter;

  // ── Alerts ───────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Confirm modal (deactivate or reactivate) ────────────
  const [confirmAction, setConfirmAction] = useState(null);

  // ── Data hook ────────────────────────────────────────────
  const {
    products,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    choices,
    fetchProducts,
    fetchProduct,
    saveProduct,
    removeProduct,
    reactivateProduct,
    setInventoryStatus,
    bulkSetCategory,
    clearError,
  } = useEmployeeProducts();

  // ── Build current params (reads refs for fresh state) ────
  const buildParams = useCallback(
    (overrides = {}) => ({
      search: searchTerm,
      is_active: activeFilterRef.current,
      page: pagination.page,
      ...overrides,
    }),
    [searchTerm, pagination.page]
  );

  // ── Debounced search ─────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProducts({ search: val, is_active: activeFilterRef.current, page: 1 });
    }, 300);
  };

  // ── Filter by active status ──────────────────────────────
  const handleFilterChange = (value) => {
    setActiveFilter(value);
    fetchProducts(buildParams({ is_active: value, page: 1 }));
  };

  // ── Pagination ───────────────────────────────────────────
  const handlePageChange = (page) => {
    setSelectedIds(new Set());
    fetchProducts(buildParams({ page }));
  };

  // ── Bulk selection ───────────────────────────────────────
  const allPageIds = products.map((p) => p.id);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));
  const someSelected = allPageIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allPageIds));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkApply = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    try {
      await bulkSetCategory([...selectedIds], bulkCategoryIds);
      setSelectedIds(new Set());
      setBulkCategoryIds([]);
      setAlert({ type: 'success', message: `Categories updated for ${count} product(s).` });
      fetchProducts(buildParams({ page: 1 }));
    } catch {
      setAlert({ type: 'error', message: 'Failed to update categories.' });
    }
  };

  // ── Row click → edit ─────────────────────────────────────
  const handleRowClick = async (row) => {
    try {
      const full = await fetchProduct(row.id);
      setEditingProduct(full);
      setView('form');
      setAlert(null);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load product details.' });
    }
  };

  // ── Add new ──────────────────────────────────────────────
  const handleAddNew = () => {
    setEditingProduct(null);
    setView('form');
    setAlert(null);
  };

  // ── Cancel form ──────────────────────────────────────────
  const handleCancel = () => {
    setEditingProduct(null);
    setView('list');
  };

  // ── Save (create or update) ──────────────────────────────
  const handleSave = async (data) => {
    try {
      const id = editingProduct?.id || null;
      await saveProduct(data, id);
      setView('list');
      setEditingProduct(null);
      setAlert({
        type: 'success',
        message: id ? 'Product updated successfully.' : 'Product created successfully.',
      });
      fetchProducts(buildParams({ page: 1 }));
    } catch (err) {
      setAlert({
        type: 'error',
        message: err?.message || 'Failed to save product.',
      });
    }
  };

  // ── Inventory status ─────────────────────────────────────
  const handleInventoryStatusChange = async (productId, newStatus) => {
    try {
      await setInventoryStatus(productId, newStatus);
      const label = INVENTORY_STATUS_OPTIONS.find((o) => o.value === newStatus)?.label || newStatus;
      setAlert({ type: 'success', message: `Inventory status updated to "${label}".` });
    } catch {
      setAlert({ type: 'error', message: 'Failed to update inventory status.' });
    }
  };

  // ── Deactivate / Reactivate ──────────────────────────────
  const handleStatusAction = (type) => {
    if (editingProduct) setConfirmAction({ product: editingProduct, type });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { product: target, type } = confirmAction;
    try {
      if (type === 'deactivate') {
        await removeProduct(target.id);
      } else {
        await reactivateProduct(target.id);
      }
      setConfirmAction(null);
      setView('list');
      setEditingProduct(null);
      setAlert({
        type: 'success',
        message: type === 'deactivate'
          ? `"${target.name}" deactivated.`
          : `"${target.name}" reactivated.`,
      });
      fetchProducts(buildParams({ page: 1 }));
    } catch {
      setAlert({
        type: 'error',
        message: `Failed to ${type} product.`,
      });
      setConfirmAction(null);
    }
  };

  // ── Clean up debounce timer on unmount ──────────────────
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // ── Clear hook errors into alert ─────────────────────────
  useEffect(() => {
    if (error) {
      setAlert({ type: 'error', message: error.message || 'An error occurred.' });
      clearError();
    }
  }, [error, clearError]);

  // ── Table columns ────────────────────────────────────────
  const columns = [
    {
      key: '_select',
      label: (
        <input
          type="checkbox"
          className="products-checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
          onChange={toggleSelectAll}
          aria-label="Select all products"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      sortable: false,
      render: (_val, row) => (
        <input
          type="checkbox"
          className="products-checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          aria-label={`Select ${row.name}`}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    { key: 'name', label: 'Product' },
    {
      key: 'categories_detail',
      label: 'Category',
      sortable: false,
      render: (val) =>
        val && val.length > 0
          ? val.map((c) => c.label).join(', ')
          : <span style={{ color: '#b8b0a4' }}>—</span>,
    },
    {
      key: 'variants',
      label: 'Variants',
      sortable: false,
      render: (_val, row) => {
        const total = row.variants?.length || 0;
        const active = row.active_variants_count ?? 0;
        return (
          <span className="products-variant-count">
            {active}<span className="products-variant-count__sep">/</span>{total}
          </span>
        );
      },
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`emp-list__badge emp-list__badge--${val ? 'active' : 'inactive'}`}>
          {val ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'inventory_status',
      label: 'Inventory',
      sortable: false,
      render: (val, row) => (
        <select
          className={`products-inventory-select products-inventory-select--${(val || 'in_stock').replace(/_/g, '-')}`}
          value={val || 'in_stock'}
          onChange={(e) => {
            e.stopPropagation();
            handleInventoryStatusChange(row.id, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={isSaving}
          aria-label={`Inventory status for ${row.name}`}
        >
          {INVENTORY_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'grain_varieties_detail',
      label: 'Grain Varieties',
      sortable: false,
      render: (val) =>
        val && val.length > 0
          ? (
            <div className="products-grain-pills">
              {val.map((gv) => (
                <span key={gv.id} className="emp-list__card-tag">{gv.name}</span>
              ))}
            </div>
          )
          : '\u2014',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (val) => formatDate(val),
    },
  ];

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="employee-page products-page">
      {/* ── Alert ──────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {view === 'list' ? (
        <>
          {/* ── Header ───────────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">Products</h1>
          </div>

          {/* ── Toolbar ──────────────────────────────────── */}
          <div className="emp-list__toolbar">
            <div className="emp-list__toolbar-left">
              <div className="emp-list__search-wrap">
                <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" fill="currentColor" />
                </svg>
                <input
                  type="text"
                  className="emp-list__search"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label="Search products"
                />
              </div>
              <div className="emp-list__filters">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className={`emp-list__filter ${activeFilter === f.value ? 'emp-list__filter--active' : ''}`}
                    onClick={() => handleFilterChange(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <Button variant="primary" size="small" onClick={handleAddNew}>
              + Add Product
            </Button>
          </div>

          {/* ── Bulk action bar ──────────────────────────── */}
          {selectedIds.size > 0 && (
            <div className="products-bulk-bar">
              <span className="products-bulk-bar__count">
                {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <MultiSelect
                label=""
                name="bulk_category_ids"
                options={(choices?.categories || []).map((c) => ({ id: c.id, name: c.label }))}
                value={bulkCategoryIds}
                onChange={(_, val) => setBulkCategoryIds(val)}
                placeholder="Set categories…"
              />
              <Button
                variant="primary"
                size="small"
                onClick={handleBulkApply}
                disabled={isSaving}
              >
                {isSaving ? 'Applying…' : 'Apply'}
              </Button>
              <button
                type="button"
                className="products-bulk-bar__clear"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </button>
            </div>
          )}

          {/* ── Content body ─────────────────────────────── */}
          <div className="emp-list__body">
            {loading ? (
              <Loading text="Loading products..." />
            ) : (
              <>
                {/* Desktop table (hidden on mobile) */}
                <Table
                  columns={columns}
                  data={products}
                  onRowClick={handleRowClick}
                  emptyMessage="No products found."
                  className="emp-list__table"
                />

                {/* Mobile card list (hidden on desktop) */}
                <div className="emp-list__cards">
                  {products.length === 0 ? (
                    <p className="emp-list__cards-empty">No products found.</p>
                  ) : (
                    products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        className="emp-list__card"
                        onClick={() => handleRowClick(product)}
                      >
                        <div className="emp-list__card-top">
                          <span className="emp-list__card-name">{product.name}</span>
                          <span className={`emp-list__badge emp-list__badge--${product.is_active ? 'active' : 'inactive'}`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="emp-list__card-subtitle">
                          {product.active_variants_count ?? 0} of {product.variants?.length || 0} variants active
                        </div>
                        {product.grain_varieties_detail?.length > 0 && (
                          <div className="emp-list__card-tags">
                            {product.grain_varieties_detail.map((gv) => (
                              <span key={gv.id} className="emp-list__card-tag">{gv.name}</span>
                            ))}
                          </div>
                        )}
                        <div className="emp-list__card-date">{formatDate(product.created_at)}</div>
                        <svg className="emp-list__card-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 4L10 8L6 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ))
                  )}
                </div>

                <Pagination
                  currentPage={pagination.page}
                  totalCount={pagination.count}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </>
            )}
            {isRefreshing && (
              <div className="emp-list__refreshing" />
            )}
          </div>
        </>
      ) : (
        <>
          {/* ── Form header ──────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">
              {editingProduct ? `Edit: ${editingProduct.name}` : 'Add Product'}
            </h1>
          </div>

          {/* ── Form body ────────────────────────────────── */}
          <div className="emp-list__body emp-list__body--form">
            {loading ? (
              <Loading text="Loading..." />
            ) : (
              <>
                <ProductForm
                  product={editingProduct}
                  choices={choices}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                />
                {editingProduct && (
                  editingProduct.is_active ? (
                    <div className="emp-list__danger-zone">
                      <div className="emp-list__danger-info">
                        <span className="emp-list__danger-label">Danger zone</span>
                        <span className="emp-list__danger-desc">
                          Deactivate this product and all its variants. It will no longer appear on the public site.
                        </span>
                      </div>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => handleStatusAction('deactivate')}
                        disabled={isDeleting || isSaving}
                      >
                        {isDeleting ? 'Deactivating...' : 'Deactivate Product'}
                      </Button>
                    </div>
                  ) : (
                    <div className="emp-list__reactivate-zone">
                      <div className="emp-list__danger-info">
                        <span className="emp-list__reactivate-label">Inactive</span>
                        <span className="emp-list__danger-desc">
                          This product is currently inactive and hidden from the public site.
                        </span>
                      </div>
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleStatusAction('reactivate')}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Reactivating...' : 'Reactivate Product'}
                      </Button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Confirmation modal (deactivate or reactivate) ── */}
      <Modal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.type === 'reactivate' ? 'Confirm Reactivation' : 'Confirm Deactivation'}
        size="small"
      >
        <p className="emp-list__confirm-text">
          {confirmAction?.type === 'reactivate' ? (
            <>
              Are you sure you want to reactivate <strong>{confirmAction?.product?.name}</strong>?
              It will appear on the public site again.
            </>
          ) : (
            <>
              Are you sure you want to deactivate <strong>{confirmAction?.product?.name}</strong>?
              It and all its variants will be hidden from the public site but can be reactivated later.
            </>
          )}
        </p>
        <div className="emp-list__confirm-actions">
          <Button variant="secondary" size="small" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmAction?.type === 'reactivate' ? 'primary' : 'danger'}
            size="small"
            onClick={handleConfirm}
            disabled={isDeleting || isSaving}
          >
            {confirmAction?.type === 'reactivate'
              ? (isSaving ? 'Reactivating...' : 'Reactivate')
              : (isDeleting ? 'Deactivating...' : 'Deactivate')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeProducts;
